import { Suspense, lazy, useCallback } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { usePendingPublicActionResume } from '@discovr/features';

// `PendingActionConfirmDialog` is deliberately NOT exported from this
// package's barrel (`packages/ui/src/index.ts`) — it's only ever reached
// through this dynamic import, so its code (and the shadcn Dialog primitive
// it pulls in) stays out of every bundle except the rare one that actually
// resumes a pending action. Matches this repo's existing lazy-load
// convention for tabs/drawers (see `CampaignDetailPage.tsx`).
const PendingActionConfirmDialog = lazy(() =>
  import('./PendingActionConfirmDialog').then((m) => ({ default: m.PendingActionConfirmDialog })),
);

/**
 * Bridges `usePendingPublicActionResume()` (in `@discovr/features`, which
 * cannot depend on `@discovr/ui` — see that hook's doc comment for the
 * dependency-direction constraint) to `PendingActionConfirmDialog` (in
 * `@discovr/ui`). This is the only place in the app that can see both, so
 * it's the one place this wiring can live.
 *
 * Mount once near the top of each authed app shell, inside both
 * `<AuthProvider>` and `<QueryClientProvider>` (the hook calls `useAuth()`
 * and `useQueryClient()`, both of which throw without their provider).
 * Renders nothing on the (overwhelmingly common) path where there's no
 * pending action to resume.
 */
export function PendingPublicActionResumeHost() {
  const navigate = useNavigate();
  // Injected into the hook's `navigate`/`select_pill`/`open_insights`
  // handlers as `ExecuteCtx.navigateToTab` — see
  // `usePendingPublicActionResume.ts`'s `UsePendingPublicActionResumeOptions`
  // doc comment for why the router call has to be built HERE rather than
  // inside the hook itself (`@discovr/features` has no
  // `@tanstack/react-router` dependency). `tab`/`pill` mirror the
  // `?tab=`/`?pill=` shape `useCampaignDeepLinkTab.ts` /
  // `useCampaignPillParam.ts` write; `creator`/`panel`/`sub` mirror
  // `useCampaignPanelControls.openInsights`'s own `?creator=`/`?panel=`/
  // `?sub=` shape (see `pendingPublicActionRegistry.ts`'s `openInsightsHandler`
  // — reusing that exact contract is what lets the resumed `open_insights`
  // action reopen the drawer via the page's existing deep-link reader, no
  // separate "open drawer" callback needed). All reconstructed inline since
  // this host has no campaign-detail route context of its own to reuse those
  // hooks from. `as never`: this package is router-agnostic and cannot
  // reference the app's registered route tree (same rationale as those hooks).
  const navigateToTab = useCallback(
    (target: { tab: string; pill?: string; creator?: string; panel?: string; sub?: string }) => {
      void navigate({
        to: '.',
        search: ((prev: Record<string, unknown>) => ({
          ...prev,
          tab: target.tab,
          pill: target.pill,
          creator: target.creator,
          panel: target.panel,
          sub: target.sub,
          page: undefined,
        })) as never,
      } as never);
    },
    [navigate],
  );
  const { dialog } = usePendingPublicActionResume({ navigateToTab });

  if (!dialog) return null;

  return (
    <Suspense fallback={null}>
      <PendingActionConfirmDialog open {...dialog} />
    </Suspense>
  );
}
